import { z } from 'zod';
import type { MessageDescriptor } from 'react-intl';
import type { PaymentMetaOptionDetails } from 'domain/payments/types';
import { BENEFICIARY_FIELD_CONFIG, type FieldValidation } from './config';
import type { BeneficiaryField, FormValues } from './types';

type FormatMessage = (msg: MessageDescriptor, values?: Record<string, string | number>) => string;

function applyStringValidation(schema: z.ZodString, v: FieldValidation, formatMessage: FormatMessage): z.ZodString {
  let s = schema;
  if (v.minLength)
    s = s.min(v.minLength, formatMessage({ id: 'beneficiary.validation.minLength', defaultMessage: 'Min {min} characters' }, { min: v.minLength }));
  if (v.maxLength)
    s = s.max(v.maxLength, formatMessage({ id: 'beneficiary.validation.maxLength', defaultMessage: 'Max {max} characters' }, { max: v.maxLength }));
  v.patterns?.forEach(({ pattern, message }) => {
    s = s.regex(new RegExp(pattern), formatMessage(message));
  });
  return s;
}

export function buildZodSchema(fields: BeneficiaryField[], formatMessage: FormatMessage) {
  const required = formatMessage({ id: 'input.required', defaultMessage: 'Required' });
  const invalidEmail = formatMessage({ id: 'message.invalid_email_address', defaultMessage: 'Invalid email address' });

  const shape = fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
    const v = BENEFICIARY_FIELD_CONFIG[field.name]?.validation;
    let schema: z.ZodTypeAny;

    switch (field.type) {
      case 'multi-select':
        schema = z.array(z.string()).min(1, required);
        break;
      case 'email':
        schema = applyStringValidation(z.string().min(1, required).email(invalidEmail), v ?? {}, formatMessage);
        break;
      default:
        schema = applyStringValidation(z.string().min(1, required), v ?? {}, formatMessage);
    }

    acc[field.name] = schema;
    return acc;
  }, {});

  return z.object({ ...shape, __isDefault: z.enum(['true', 'false']) });
}

export function buildDefaultValues(fields: BeneficiaryField[], initialValues?: PaymentMetaOptionDetails): FormValues {
  const base = fields.reduce<FormValues>((acc, field) => {
    if (field.type === 'multi-select') {
      acc[field.name] = Array.isArray(field.defaultValue)
        ? field.defaultValue
        : field.defaultValue
          ? [field.defaultValue as string]
          : [];
    } else {
      acc[field.name] =
        typeof field.defaultValue === 'string'
          ? field.defaultValue
          : Array.isArray(field.defaultValue) && field.defaultValue.length > 0
            ? (field.defaultValue[0] as string)
            : '';
    }
    return acc;
  }, {});

  const definedInitial = Object.fromEntries(
    Object.entries(initialValues ?? {}).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );

  return { ...base, ...definedInitial };
}
